import { useMemo } from 'react';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { getSupportUserProfilePicSrc } from '../../utils/Support/supportHelpers';
import { useAgentConfigsQuery } from './useSupportAgentConfig';
import type { ISupportAgentConfig } from './useSupportAgentConfig';

export type { ISupportAgentConfig };

export interface SupportTeamPresentation {
	teamMembers: ISupportAgentConfig[];
	agentNameMap: Record<string, string>;
	agentProfilePicMap: Record<string, string>;
	agentProfilePicEmailMap: Record<string, string>;
}

/** Query all support agent configs for a mailbox. */
export function useSupportTeamQuery(mailboxId: string) {
	return useAgentConfigsQuery(mailboxId);
}

const getAgentProfilePicSrc = (agent: ISupportAgentConfig, accountId?: string) =>
	getSupportUserProfilePicSrc({
		agentProfilePic: agent.agentProfilePic,
		userId: agent.agentId,
		accountId,
	});

export function getSupportTeamPresentation(
	teamMembers: ISupportAgentConfig[],
	accountId?: string
): SupportTeamPresentation {
	const agentNameMap: Record<string, string> = {};
	const agentProfilePicMap: Record<string, string> = {};
	const agentProfilePicEmailMap: Record<string, string> = {};

	for (const agent of teamMembers) {
		if (agent.agentId && agent.agentName) {
			agentNameMap[agent.agentId] = agent.agentName;
		}

		const profilePic = getAgentProfilePicSrc(agent, accountId);
		if (!profilePic) continue;

		if (agent.agentId) {
			agentProfilePicMap[agent.agentId] = profilePic;
		}
		if (agent.agentEmail) {
			agentProfilePicEmailMap[agent.agentEmail.trim().toLowerCase()] = profilePic;
		}
	}

	return { teamMembers, agentNameMap, agentProfilePicMap, agentProfilePicEmailMap };
}

export function useSupportTeamPresentation(mailboxId: string): SupportTeamPresentation {
	const { data } = useSupportTeamQuery(mailboxId);
	const userSession = useUserSession();
	const accountId = userSession.account?.id;
	return useMemo(() => getSupportTeamPresentation(data?.values ?? [], accountId), [accountId, data]);
}
